/* eslint-disable no-console */
/**
 * EpcShare — Editorial Calm share engine.
 *
 * Ported in spirit from `react-native-share` (MIT). The runtime is the
 * browser, so native iOS `UIActivityViewController` / Android `Intent`
 * bridges are replaced with the Web Share API plus per-platform intent
 * URLs / deep links. Public API surface, option shape, result shape,
 * Social enum, `failOnCancel` semantics, and the ShareSheet state engine
 * (idle → open → sharing → success|dismissed|error → idle) follow the
 * RN library so callers feel familiar.
 *
 * Honest web limits:
 *  - Instagram has no web share intent. On mobile we try `instagram://`
 *    deep links and fall back to copy-link + open IG. On desktop the
 *    button is disabled (same as RN throwing "Not implemented" off
 *    iOS/Android).
 *  - `failOnCancel` only fires when the underlying API surfaces a
 *    dismissal signal (Web Share API `AbortError`). Static intent links
 *    open in a new tab and resolve as `success: true` immediately.
 */
(function (root) {
  'use strict';

  // ── Social enum (mirrors react-native-share/src/types.ts#Social) ─────
  var Social = Object.freeze({
    FACEBOOK: 'facebook',
    TWITTER: 'twitter',
    PINTEREST: 'pinterest',
    INSTAGRAM: 'instagram',
    WHATSAPP: 'whatsapp',
    TELEGRAM: 'telegram',
    LINKEDIN: 'linkedin',
    REDDIT: 'reddit',
    TUMBLR: 'tumblr',
    EMAIL: 'email',
    SMS: 'sms',
    COPY_LINK: 'copylink'
  });

  // ── Platform / capability detection (mirrors helpers/platform.ts) ────
  function ua() {
    return (root.navigator && root.navigator.userAgent) || '';
  }
  function isIOS() {
    return /iPhone|iPad|iPod/i.test(ua());
  }
  function isAndroid() {
    return /Android/i.test(ua());
  }
  function isMobile() {
    return isIOS() || isAndroid();
  }
  function capabilities() {
    var nav = root.navigator || {};
    return {
      webShare: typeof nav.share === 'function',
      webShareFiles:
        typeof nav.canShare === 'function' && typeof nav.share === 'function',
      clipboard: !!(nav.clipboard && nav.clipboard.writeText),
      mobile: isMobile(),
      ios: isIOS(),
      android: isAndroid()
    };
  }

  // ── Heuristic "isPackageInstalled" (mirrors index.tsx#isPackageInstalled).
  // The web has no real way to query installed apps; we only return
  // `true` for platforms that have a viable deep-link path on this UA.
  function isLikelyInstalled(social) {
    if (!isMobile()) return false;
    switch (social) {
      case Social.INSTAGRAM:
      case Social.WHATSAPP:
      case Social.TELEGRAM:
      case Social.SMS:
        return true;
      default:
        return true;
    }
  }

  // ── Option normalization (mirrors helpers/options.ts) ────────────────
  function normalizeOpenOptions(opts) {
    var o = Object.assign({}, opts || {});
    if (o.url && !o.urls) {
      o.urls = [o.url];
    }
    if (typeof o.failOnCancel === 'undefined') o.failOnCancel = true;
    return o;
  }
  function normalizeSingleOptions(opts) {
    var o = Object.assign({}, opts || {});
    if (o.url) o.urls = [o.url];
    if (!o.social) throw new Error('EpcShare.shareSingle: `social` is required');
    return o;
  }

  // ── Intent URL builders (web equivalent of native shareSingle) ───────
  function enc(v) {
    return encodeURIComponent(v == null ? '' : String(v));
  }
  var INTENT_BUILDERS = {};
  INTENT_BUILDERS[Social.FACEBOOK] = function (o) {
    return 'https://www.facebook.com/sharer/sharer.php?u=' + enc(o.url);
  };
  INTENT_BUILDERS[Social.TWITTER] = function (o) {
    return (
      'https://twitter.com/intent/tweet?url=' +
      enc(o.url) +
      '&text=' +
      enc(o.title || o.message || '')
    );
  };
  INTENT_BUILDERS[Social.PINTEREST] = function (o) {
    return (
      'https://pinterest.com/pin/create/button/?url=' +
      enc(o.url) +
      '&media=' +
      enc(o.image || '') +
      '&description=' +
      enc(o.title || o.message || '')
    );
  };
  INTENT_BUILDERS[Social.WHATSAPP] = function (o) {
    var text = [o.title, o.url].filter(Boolean).join(' ');
    return 'https://api.whatsapp.com/send?text=' + enc(text);
  };
  INTENT_BUILDERS[Social.TELEGRAM] = function (o) {
    return (
      'https://t.me/share/url?url=' +
      enc(o.url) +
      '&text=' +
      enc(o.title || o.message || '')
    );
  };
  INTENT_BUILDERS[Social.LINKEDIN] = function (o) {
    return 'https://www.linkedin.com/sharing/share-offsite/?url=' + enc(o.url);
  };
  INTENT_BUILDERS[Social.REDDIT] = function (o) {
    return (
      'https://www.reddit.com/submit?url=' +
      enc(o.url) +
      '&title=' +
      enc(o.title || '')
    );
  };
  INTENT_BUILDERS[Social.TUMBLR] = function (o) {
    return (
      'https://www.tumblr.com/widgets/share/tool?canonicalUrl=' +
      enc(o.url) +
      '&title=' +
      enc(o.title || '')
    );
  };
  INTENT_BUILDERS[Social.EMAIL] = function (o) {
    return (
      'mailto:' +
      enc(o.recipient || '') +
      '?subject=' +
      enc(o.subject || o.title || '') +
      '&body=' +
      enc((o.message ? o.message + '\n\n' : '') + (o.url || ''))
    );
  };
  INTENT_BUILDERS[Social.SMS] = function (o) {
    var sep = isIOS() ? '&' : '?';
    var body = [o.title, o.url].filter(Boolean).join(' ');
    return 'sms:' + (o.recipient || '') + sep + 'body=' + enc(body);
  };

  // ── Result shapes (mirror ShareOpenResult / ShareSingleResult) ───────
  function ok(message) {
    return { success: true, message: message || 'OK' };
  }
  function dismissed(message) {
    return { success: false, dismissedAction: true, message: message || 'Dismissed' };
  }

  // ── Engine: top-level `open` (mirrors RNShare.open) ──────────────────
  function open(options) {
    var opts = normalizeOpenOptions(options);
    var caps = capabilities();

    if (caps.webShare) {
      var payload = {
        title: opts.title,
        text: opts.message,
        url: opts.url
      };
      return root.navigator.share(payload).then(
        function () {
          return ok('Shared via Web Share');
        },
        function (err) {
          var isAbort = err && (err.name === 'AbortError' || err.code === 20);
          if (isAbort) {
            if (opts.failOnCancel) {
              var e = new Error('User did not share');
              e.dismissedAction = true;
              throw e;
            }
            return dismissed('User cancelled');
          }
          throw err;
        }
      );
    }

    // No Web Share API → resolve dismissed so callers can open the sheet.
    return Promise.resolve(dismissed('Web Share API unavailable'));
  }

  // ── Engine: `shareSingle` (mirrors RNShare.shareSingle) ──────────────
  function shareSingle(options) {
    var opts;
    try {
      opts = normalizeSingleOptions(options);
    } catch (e) {
      return Promise.reject(e);
    }

    if (opts.social === Social.COPY_LINK) {
      return copyLink(opts.url);
    }

    if (opts.social === Social.INSTAGRAM) {
      return shareToInstagram(opts);
    }

    var build = INTENT_BUILDERS[opts.social];
    if (!build) {
      return Promise.reject(new Error('EpcShare: unsupported social `' + opts.social + '`'));
    }
    var href = build(opts);
    var isMail = opts.social === Social.EMAIL || opts.social === Social.SMS;
    openIntent(href, isMail ? '_self' : '_blank');
    return Promise.resolve(ok('Opened ' + opts.social));
  }

  function openIntent(href, target) {
    try {
      var win = root.open(href, target || '_blank', 'noopener,noreferrer');
      if (!win && target !== '_self') {
        root.location.href = href;
      }
    } catch (e) {
      root.location.href = href;
    }
  }

  // ── Instagram: web-honest implementation ─────────────────────────────
  function shareToInstagram(opts) {
    if (!isMobile()) {
      return Promise.reject(
        new Error('Instagram sharing is only available on mobile devices')
      );
    }
    // Best effort: copy the URL so user can paste in IG, then open the app.
    return copyLink(opts.url)
      .catch(function () { return ok('proceeding'); })
      .then(function () {
        var deep = opts.image
          ? 'instagram://library?AssetPath=' + enc(opts.image)
          : 'instagram://app';
        openIntent(deep, '_self');
        return ok('Opened Instagram (link copied to clipboard)');
      });
  }

  // ── Clipboard helper ─────────────────────────────────────────────────
  function copyLink(url) {
    if (!url) return Promise.reject(new Error('No URL to copy'));
    var nav = root.navigator || {};
    if (nav.clipboard && nav.clipboard.writeText) {
      return nav.clipboard.writeText(url).then(function () {
        return ok('Link copied');
      });
    }
    try {
      var ta = root.document.createElement('textarea');
      ta.value = url;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      root.document.body.appendChild(ta);
      ta.select();
      root.document.execCommand('copy');
      root.document.body.removeChild(ta);
      return Promise.resolve(ok('Link copied'));
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // ── ShareSheet controller (mirrors components/ShareSheet.tsx) ────────
  // States: idle | open | sharing | success | dismissed | error
  function ShareSheet(rootEl, config) {
    this.root = rootEl;
    this.config = config || {};
    this.dialog = rootEl.querySelector('[data-epc-share-dialog]');
    this.backdrop = rootEl.querySelector('[data-epc-share-backdrop]');
    this.closeBtn = rootEl.querySelector('[data-epc-share-close]');
    this.status = rootEl.querySelector('[data-epc-share-status]');
    this.buttonsHost = rootEl.querySelector('[data-epc-share-buttons]');
    // Trigger lives OUTSIDE the modal root (so it stays visible while
    // root is `hidden`). Look it up by aria-controls=<root id>.
    this.triggers = [];
    if (rootEl.id && root.document) {
      var sel = '[data-epc-share-trigger][aria-controls="' + rootEl.id + '"]';
      this.triggers = Array.prototype.slice.call(root.document.querySelectorAll(sel));
    }
    // Fallback: any trigger that's a descendant (legacy markup).
    if (!this.triggers.length) {
      var inside = rootEl.querySelector('[data-epc-share-trigger]');
      if (inside) this.triggers = [inside];
    }
    this.state = 'idle';
    this._prevFocus = null;
    this._onKey = this._onKey.bind(this);
    this._onTrigger = this._onTrigger.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onBackdrop = this._onBackdrop.bind(this);
    this._onButton = this._onButton.bind(this);
    this._init();
  }

  ShareSheet.prototype._init = function () {
    this._renderButtons();
    for (var i = 0; i < this.triggers.length; i++) {
      this.triggers[i].addEventListener('click', this._onTrigger);
    }
    if (this.closeBtn) this.closeBtn.addEventListener('click', this._onClose);
    if (this.backdrop) this.backdrop.addEventListener('click', this._onBackdrop);
    if (this.buttonsHost) this.buttonsHost.addEventListener('click', this._onButton);
  };

  ShareSheet.prototype._setState = function (next, message) {
    this.state = next;
    if (this.status) {
      this.status.textContent = message || '';
    }
    this.root.setAttribute('data-state', next);
  };

  ShareSheet.prototype._renderButtons = function () {
    if (!this.buttonsHost) return;
    var enabled = this.config.enabled || [];
    var caps = capabilities();
    var html = '';
    for (var i = 0; i < enabled.length; i++) {
      var item = enabled[i];
      var disabled = false;
      var hint = '';
      if (item.social === Social.INSTAGRAM && !caps.mobile) {
        disabled = true;
        hint = 'Mobile only';
      }
      html +=
        '<button type="button" class="epc-share-btn" ' +
        'data-social="' + item.social + '" ' +
        (disabled ? 'disabled aria-disabled="true" title="' + hint + '" ' : '') +
        'aria-label="Share via ' + item.label + '">' +
        '<span class="epc-share-btn__icon" aria-hidden="true">' + (item.icon || '') + '</span>' +
        '<span class="epc-share-btn__label">' + item.label + '</span>' +
        (hint ? '<span class="epc-share-btn__hint">' + hint + '</span>' : '') +
        '</button>';
    }
    this.buttonsHost.innerHTML = html;
  };

  ShareSheet.prototype._onTrigger = function (e) {
    e.preventDefault();
    e.__epcHandled = true;
    var self = this;
    // First attempt: native Web Share. If it returns dismissed because
    // unavailable, fall back to the in-page sheet (mirrors RN behavior
    // of presenting a custom sheet when the system sheet isn't viable).
    open({
      title: this.config.title,
      message: this.config.message,
      url: this.config.url,
      failOnCancel: false
    })
      .then(function (res) {
        if (res && res.success) {
          self._setState('success', 'Shared');
          return;
        }
        // No native sheet OR user dismissed — show the custom sheet.
        self.openSheet();
      })
      .catch(function () {
        self.openSheet();
      });
  };

  ShareSheet.prototype.openSheet = function () {
    if (this.state === 'open') return;
    this._prevFocus = root.document.activeElement;
    this._setState('open');
    this.root.hidden = false;
    if (this.dialog) {
      this.dialog.setAttribute('aria-hidden', 'false');
      var focusable = this.dialog.querySelector('button:not([disabled])');
      if (focusable) focusable.focus();
    }
    root.document.addEventListener('keydown', this._onKey);
  };

  ShareSheet.prototype.closeSheet = function () {
    if (this.state === 'idle') return;
    this._setState('idle');
    this.root.hidden = true;
    if (this.dialog) this.dialog.setAttribute('aria-hidden', 'true');
    root.document.removeEventListener('keydown', this._onKey);
    if (this._prevFocus && this._prevFocus.focus) this._prevFocus.focus();
  };

  // Web equivalent of `BackHandler.addEventListener('hardwareBackPress', …)`
  // from components/ShareSheet.tsx — Escape closes the sheet.
  ShareSheet.prototype._onKey = function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeSheet();
    }
  };

  ShareSheet.prototype._onClose = function (e) {
    e.preventDefault();
    this.closeSheet();
  };

  ShareSheet.prototype._onBackdrop = function (e) {
    if (e.target === this.backdrop) this.closeSheet();
  };

  ShareSheet.prototype._onButton = function (e) {
    var btn = e.target.closest('[data-social]');
    if (!btn || btn.disabled) return;
    var social = btn.getAttribute('data-social');
    var self = this;
    this._setState('sharing', 'Opening ' + social + '…');
    shareSingle({
      social: social,
      url: this.config.url,
      title: this.config.title,
      message: this.config.message,
      image: this.config.image,
      subject: this.config.title
    }).then(
      function (res) {
        self._setState('success', res.message || 'Shared');
        if (social === Social.COPY_LINK) {
          // Keep the sheet open briefly so the user sees the toast.
          setTimeout(function () { self.closeSheet(); }, 1200);
        } else {
          setTimeout(function () { self.closeSheet(); }, 250);
        }
      },
      function (err) {
        self._setState('error', (err && err.message) || 'Share failed');
      }
    );
  };

  // ── Auto-init on DOM ready ───────────────────────────────────────────
  function readConfig(el) {
    var node = el.querySelector('script[type="application/json"][data-epc-share-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (e) {
      console.warn('[EpcShare] bad config JSON', e);
      return {};
    }
  }

  function init(scope) {
    var doc = scope || root.document;
    var roots = doc.querySelectorAll('[data-epc-share]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__epcShare) continue;
      var cfg = readConfig(roots[i]);
      roots[i].__epcShare = new ShareSheet(roots[i], cfg);
    }
  }

  // Document-level delegated trigger handler. Catches the case where a
  // trigger is added (or re-rendered by Shopify section reload) after
  // its ShareSheet was instantiated.
  function onDocumentClick(e) {
    var trigger = e.target.closest && e.target.closest('[data-epc-share-trigger]');
    if (!trigger) return;
    var controls = trigger.getAttribute('aria-controls');
    if (!controls) return;
    var rootEl = root.document.getElementById(controls);
    if (!rootEl) return;
    if (!rootEl.__epcShare) {
      var cfg = readConfig(rootEl);
      rootEl.__epcShare = new ShareSheet(rootEl, cfg);
    }
    // If the per-trigger listener already fired, this would double-fire;
    // gate via a flag set in _onTrigger.
    if (e.__epcHandled) return;
    e.__epcHandled = true;
    rootEl.__epcShare._onTrigger(e);
  }

  if (root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', function () { init(); });
    } else {
      init();
    }
    // Document-level delegated click for triggers — safety net for
    // re-rendered sections and any markup ordering quirks.
    root.document.addEventListener('click', onDocumentClick);
    // Re-init on Shopify section reload in the theme editor.
    if (root.Shopify) {
      root.document.addEventListener('shopify:section:load', function (e) {
        init(e.target);
      });
    }
  }

  // ── Public API ───────────────────────────────────────────────────────
  root.EpcShare = {
    Social: Social,
    open: open,
    shareSingle: shareSingle,
    copyLink: copyLink,
    capabilities: capabilities,
    isLikelyInstalled: isLikelyInstalled,
    ShareSheet: ShareSheet,
    init: init
  };
})(typeof window !== 'undefined' ? window : this);
